"use client";
import React from "react";
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate, spring } from "remotion";
import { PhoneMock, type Beats } from "./HypeButton";

// ─────────────────────────────────────────────────────────────────────────────
// DEVA KATHA — HYPE CTA (reusable)
//
// The YouTube "Hype" phone CTA, extracted from KanwarYatraOverlays so every CTA beat
// in every devotional production uses ONE implementation. Yash 2026-07-28:
// "at all 3 visual places, the same hype cta we have created should be used."
//
// Replaces the DevaKathaLS2/LS5 pills. A pill and the phone at the same beat collide —
// use one or the other.
//
// ── HOW TO USE (per CTA beat) ───────────────────────────────────────────────
//   <Sequence from={S(187.76)} durationInFrames={S(11.50)}>   // NUMERIC LITERALS, see below
//     <DevaKathaHypeCta screenSrc={staticFile("kanwar_cta1.mp4")} title="…" … />
//   </Sequence>
//
// 🔥 THE SEQUENCE TIMES MUST BE NUMERIC LITERALS, never consts or variables.
//    build_overlay_windows.py extracts overlay windows by regex-matching numeric literals
//    in the .tsx. With `S(SOME_CONST)` it finds NO window, and factory_4k_full then
//    composites the 4K master with the CTA MISSING — only visible after a paid render.
//    (This bit Kanwar on 2026-07-28 and was caught during lock, not after.)
//
// ── THE BEAT GRID ───────────────────────────────────────────────────────────
// Beats are LOCAL to the <Sequence> (useCurrentFrame is 0-based inside one), never absolute.
// The four actions sit on an EVEN grid. The step is not free: where two actions are welded
// to spoken words (सब्सक्राइब → शेयर), any even grid hitting both must use step = gap / n.
// Kanwar's outro: gap 3.77s, n=2 → 1.885s. A 3.77s step was measured even but read as draggy
// (each badge lives ~1.4s → ~2.3s of dead air); 1.885s reads as a rhythm.
// LIKE must precede HYPE — the Hype chip is locked until the like unlocks it.
export const CTA_STEP = 1.885;   // the house rhythm
export const CTA_LEAD = 1.66;    // phone rises this long before the FIRST spoken CTA word
export const CTA_DUR  = 11.50;   // house window: rise → 4 actions → leaderboard chip → fall
export const CTA_TAIL = 4.19;    // after the LAST tap: badge(+1.285) → hold → fall → slack

// ── ⛔ THE ANCHOR LAW (Yash, 2026-08-13, after Vaishno Devi shipped 14s late) ───────────
// The window is anchored to the FIRST CTA word spoken in the cluster — लाइक / हाइप /
// सब्सक्राइब / कमेंट / शेयर, whichever comes first — and runs THROUGH the last one.
// NEVER assume सब्सक्राइब leads: scripts routinely say "लाइक और हाइप कर दीजिए" ten
// seconds before "सब्सक्राइब", and a सब्सक्राइब-anchored window means the phone is not
// even on screen when the narrator asks for the like. Use ctaWindowFromWords() +
// ctaBeatsFromWords(); `ctaBeats()` below is the FIXED-RHYTHM fallback for the one case
// where a single CTA word is spoken.
export type SpokenCta = {
  like?: number; hype?: number; sub?: number; share?: number;   // ABSOLUTE spoken times (WTS)
};

/** Window for one CTA cluster, from the spoken times. `from` is the <Sequence from={S(...)}>
 *  and `dur` its durationInFrames — BOTH must be written into the .tsx as numeric literals,
 *  and `dur` also drives the seg cut (spec.cta_windows = [[from, dur], …]). */
export function ctaWindowFromWords(
  spoken: SpokenCta, runtime?: number,
): { from: number; dur: number; clamped: boolean } {
  const ts = Object.values(spoken).filter((n): n is number => typeof n === "number");
  if (!ts.length) throw new Error("ctaWindowFromWords: no spoken CTA word given");
  const first = Math.min(...ts), last = Math.max(...ts);
  const dur = +Math.max(CTA_DUR, last - first + CTA_LEAD + CTA_TAIL).toFixed(2);
  let from = +(first - CTA_LEAD).toFixed(2);
  // An outro ask can sit closer to the end than the phone is long. Pin it at the ceiling and
  // SAY SO — the taps then land early, which is honest; running past the runtime is not
  // (the inner OffthreadVideo has no frames and the paid render dies).
  const clamped = runtime != null && from + dur > runtime;
  if (clamped) from = +Math.max(0, runtime! - dur).toFixed(2);
  return { from, dur, clamped };
}

/** Beats welded to the spoken words. Every word present in `spoken` lands ON its syllable;
 *  the rest fill in on the house step. LIKE always precedes HYPE (the Hype chip is locked
 *  until the like unlocks it), so an unspoken like is placed BEFORE a spoken hype. */
export function ctaBeatsFromWords(
  spoken: SpokenCta, windowStart: number, step: number = CTA_STEP,
): Beats {
  const L = (t?: number) => (typeof t === "number" ? +(t - windowStart).toFixed(3) : undefined);
  let like = L(spoken.like), hype = L(spoken.hype), sub = L(spoken.sub), share = L(spoken.share);
  const welded = [like, hype, sub, share].filter((n): n is number => n != null);
  if (!welded.length) throw new Error("ctaBeatsFromWords: no spoken CTA word given");
  let cursor = Math.max(...welded);
  const next = () => (cursor = +(cursor + step).toFixed(3));

  if (like == null) like = hype != null ? +Math.max(0.35, hype - step).toFixed(3) : next();
  if (hype == null) hype = +Math.max(next(), like + step).toFixed(3);
  if (sub  == null) sub  = next();
  // SHARE stays inert unless spoken — an unspoken share tap is a lie the viewer can see.
  const last = Math.max(like, hype, sub, share ?? 0);
  return {
    sub, like, hype, ...(share != null ? { share } : {}),
    unlock: +Math.min(like + 0.405, hype - 0.35).toFixed(3),   // caused by the like
    rotate: +Math.min(like + 0.805, hype - 0.15).toFixed(3),
    badge:  +(last + 1.285).toFixed(3),
  } as Beats;
}

/** FIXED-RHYTHM FALLBACK — only when exactly ONE CTA word is spoken in the cluster.
 *  `subscribeAbs` is that word's time; the sequence starts at `subscribeAbs - CTA_LEAD`.
 *  Order: SUBSCRIBE → LIKE → SHARE → HYPE. Shipped comps (Kanwar…Vrindavan) use this. */
export function ctaBeats(step: number = CTA_STEP): Beats {
  const sub = CTA_LEAD;
  return {
    sub,
    like:   +(sub + step).toFixed(3),
    unlock: +(sub + step + 0.405).toFixed(3),   // caused by the like, follows straight after
    rotate: +(sub + step + 0.805).toFixed(3),
    share:  +(sub + 2 * step).toFixed(3),
    hype:   +(sub + 3 * step).toFixed(3),
    badge:  +(sub + 3 * step + 1.285).toFixed(3),
  };
}

export const DevaKathaHypeCta: React.FC<{
  screenSrc: string;        // ~12s cut of THIS production at THIS moment (phone's inner player)
  title: string;            // the video's real title (front portion — the phone truncates)
  commentHandle: string;    // production-specific; never ship the Vitthal default
  commentText: string;
  beats?: Beats;
  hypedExtraHold?: number;  // HYPED is last, so a longer tail costs nothing downstream
  scale?: number;           // 0.58 = the shipped size; raise for legibility, not for drama
}> = ({
  screenSrc, title, commentHandle, commentText,
  beats = ctaBeats(), hypedExtraHold = 1.2, scale = 0.58,
}) => {
  const frame = useCurrentFrame();
  const { fps, height } = useVideoConfig();
  const rise = spring({ frame, fps, config: { damping: 16, stiffness: 90 }, durationInFrames: Math.round(0.9 * fps) });
  const OUT_S = beats.badge + 1.3;
  const fall = spring({ frame: frame - Math.round(OUT_S * fps), fps,
                        config: { damping: 18, stiffness: 90 }, durationInFrames: Math.round(0.8 * fps) });
  const y = interpolate(rise, [0, 1], [height, 0]) + interpolate(fall, [0, 1], [0, height]);
  // Scale the WHOLE phone — never squash the inner player, it must stay 16:9.
  const CONTENT_H = 716;
  const top = 704 - CONTENT_H * scale - 6;
  return (
    <AbsoluteFill style={{ overflow: "hidden" }}>
      <div style={{ position: "absolute", right: 34, top,
                    transform: `translateY(${y}px) scale(${scale})`, transformOrigin: "top right" }}>
        <PhoneMock
          beats={beats} showUpNext={false} fadeOut={false}
          screenSrc={screenSrc} title={title}
          theme="light" flashLabels
          commentHandle={commentHandle} commentText={commentText}
          hypedExtraHold={hypedExtraHold}
        />
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
